import { clearTenantCookie } from 'src/context/tenantCookie';
import { isContextMismatch } from './errors';

/**
 * Stale-context recovery (design spec §6): when the tenant cookie points at a
 * deleted branch/restaurant/organization, tenant APIs answer 403 with a
 * CONTEXT_MISMATCH-family code. Clear the cookie and bounce to `/`, where the
 * root ContextLoader re-resolves the default ladder and rewrites the cookie.
 * Without this, a stale cookie dead-ends the app for its full 180-day max-age.
 */

const RECOVERY_LOCK_MS = 2000;

// Pages whose own loaders don't depend on the tenant context; a 403 there is
// either an auth flow's doing or happens after `location.href` already kicked
// off the recovery navigation.
const CONTEXT_SAFE_PREFIXES = [
  '/sign-in',
  '/sign-up',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/accept-invitation',
  '/onboarding',
];

/**
 * Held in sessionStorage rather than a module variable: recovery ends in a full page
 * reload, which would wipe in-memory state and hand the reloaded page a fresh lock — so
 * the guard meant to stop a recovery loop could never actually see one.
 */
const RECOVERY_LOCK_KEY = 'restavo_tenant_recovery_at';

function readLastRecoveryAt(): number {
  try {
    return Number(sessionStorage.getItem(RECOVERY_LOCK_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeLastRecoveryAt(at: number): void {
  try {
    sessionStorage.setItem(RECOVERY_LOCK_KEY, String(at));
  } catch {
    // Private-mode storage failures just fall back to the pathname guards below.
  }
}

export function resetTenantRecoveryLock(): void {
  try {
    sessionStorage.removeItem(RECOVERY_LOCK_KEY);
  } catch {
    // nothing to reset when storage is unavailable
  }
}

function shouldRecover(pathname: string): boolean {
  if (pathname === '/') return false;
  if (CONTEXT_SAFE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return false;
  return Date.now() - readLastRecoveryAt() >= RECOVERY_LOCK_MS;
}

/**
 * Handles one axios error if it belongs to the context-mismatch family.
 * Returns true when recovery was triggered. The once-per-burst lock plus the
 * `pathname === '/'` guard stop the reload loop when even the fallback context
 * keeps 403ing.
 */
export function recoverFromStaleTenantContext(error: unknown): boolean {
  if (!isContextMismatch(error)) return false;
  if (!shouldRecover(location.pathname)) return false;
  writeLastRecoveryAt(Date.now());
  clearTenantCookie();
  location.href = '/';
  return true;
}
